import { useCallback, useEffect, useState } from 'react';
import { ProcessingRuleItem } from '@common-fe/common-fe';
import _ from 'lodash';

import { CARD_PURSE_LOCK, CARD_PURSE_UNLOCK } from '../ProcessingRuleModal.constants';

export interface TierField {
  id?: string;
  name: string;
  payoutDefinition: string;
  payoutDefinitionName: string;
  aggregateTrigger?: string;
  embeddedTrigger?: string;
  isSendTriggerNotification?: boolean;
  displayName?: boolean;
  nameToDisplay?: string;
  planId?: string;
  lockCardPurse?: string;
  planIdsForCardPurseLock?: string[];
}

const DEFAULT_TIER_FIELD: TierField = {
  id: '',
  name: '',
  payoutDefinition: '',
  payoutDefinitionName: '',
  aggregateTrigger: '',
  embeddedTrigger: '',
  displayName: false,
  nameToDisplay: '',
  planId: '',
  lockCardPurse: CARD_PURSE_UNLOCK,
  planIdsForCardPurseLock: [],
};

interface Params {
  visible?: boolean;
  updatedElement?: ProcessingRuleItem;
}

export default ({ visible, updatedElement }: Params) => {
  const [fieldsValues, setFieldsValues] = useState<TierField[]>([
    {
      ...DEFAULT_TIER_FIELD,
      id: _.uniqueId(),
    },
  ]);

  useEffect(() => {
    if (updatedElement?.tiers) {
      const copiedTiers: TierField[] = updatedElement.tiers.map((el) => {
        const familyTrigger = el.tierTriggers?.find((t) => t.triggerCategoryType === 'FAMILY');
        const embeddedTrigger = el.tierTriggers?.find((t) => t.triggerCategoryType === 'EMBEDDED');
        return {
          id: el.id,
          name: el.name,
          payoutDefinition: el.payoutDefinition.id,
          payoutDefinitionName: el.payoutDefinition.name,
          aggregateTrigger: familyTrigger?.id || '',
          embeddedTrigger: embeddedTrigger?.id || '',
          displayName: !!el.displayName,
          nameToDisplay: el.displayName,
          planId: el.planId,
          isSendTriggerNotification: el.isSendTriggerNotification,
          lockCardPurse: el.isLockPurse ? CARD_PURSE_LOCK : CARD_PURSE_UNLOCK,
          planIdsForCardPurseLock: el.planIdsForCardPurseLock || [],
        };
      });
      setFieldsValues(copiedTiers);
    } else if (!updatedElement) {
      setFieldsValues([{ ...DEFAULT_TIER_FIELD, id: _.uniqueId() }]);
    }
  }, [visible, updatedElement]);

  const getFieldValuesById = useCallback(
    (id?: string) => fieldsValues.find((field) => field.id === id),
    [fieldsValues]
  );

  const handleChangeValues = useCallback(
    (id?: string) => (values: Partial<TierField>) => {
      const newList = fieldsValues.map((item) => {
        if (item.id === id) {
          const isPayoutDefinitionChanged =
            item.payoutDefinition !== values.payoutDefinition || values.payoutDefinition === '';
          return {
            ...item,
            ...values,
            ...(isPayoutDefinitionChanged ? { planId: '' } : {}),
          };
        }
        return item;
      });
      if (!_.isEqual(newList, fieldsValues)) {
        setFieldsValues(newList);
      }
    },
    [fieldsValues]
  );

  const handleRemoveById = useCallback(
    (id: string) => {
      setFieldsValues(fieldsValues.filter((item) => item.id !== id));
    },
    [fieldsValues]
  );

  const handleClearValues = useCallback(
    (id: string) => {
      setFieldsValues(
        fieldsValues.map((item) =>
          id === item.payoutDefinition
            ? { ...item, payoutDefinition: '', payoutDefinitionName: '' }
            : { ...item }
        )
      );
    },
    [fieldsValues]
  );

  const handleAddNewTier = useCallback(() => {
    setFieldsValues([...fieldsValues, { ...DEFAULT_TIER_FIELD, id: _.uniqueId() }]);
  }, [fieldsValues]);

  return {
    handleChangeValues,
    handleRemoveById,
    getFieldValuesById,
    fieldsValues,
    handleAddNewTier,
    handleClearValues,
  };
};
